import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Chip,
  Field,
  Fieldset,
  Heading,
  Label,
  Link,
  Paragraph,
  Search,
  Table,
  Tag,
} from '@digdir/designsystemet-react';
import type { HrWorkEnvironmentOverview } from '../../../../api/hrWorkEnvironment';
import {
  COVERAGE_COLORS,
  filterCount,
  filterTeams,
  leadersText,
  nextIsLate,
  nextText,
  stepText,
  TEAM_FILTERS,
  TeamQuery,
  teamRoute,
} from '../Overview/hrOverviewView';
import { OverviewSubpage } from '../Overview/OverviewSubpage';
import classes from '../Overview/Overview.module.css';

const COLUMNS = ['team', 'leader', 'safetyRep', 'status', 'step', 'next'];

/** The column for safety representatives only where the organisation uses them */
const columnsFor = (usesSafetyReps: boolean) =>
  COLUMNS.filter((column) => usesSafetyReps || column !== 'safetyRep');

/** Every team, filtered by status or a search for a team or a person */
const Teams = ({
  overview,
}: {
  overview: HrWorkEnvironmentOverview;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState<TeamQuery>({ filter: 'all', search: '' });
  const total = overview.teams.length;
  const shown = filterTeams(overview.teams, query);
  return (
    <section aria-labelledby="teams-heading" className={classes.card}>
      <Heading level={2} data-size="xs" id="teams-heading" aria-live="polite">
        {shown.length === total
          ? t('hrOverview.teamCount', { count: total })
          : t('hrOverview.teams.shown', { shown: shown.length, total })}
      </Heading>
      <div className={classes.toolbar}>
        <Fieldset data-size="sm">
          <Fieldset.Legend>{t('hrOverview.teams.filterLabel')}</Fieldset.Legend>
          <div className={classes.filters}>
            {TEAM_FILTERS.map((filter) => (
              <Chip.Radio
                key={filter}
                name="team-filter"
                checked={query.filter === filter}
                onChange={() => setQuery({ ...query, filter })}
              >
                {t(`hrOverview.teams.filters.${filter}`, {
                  count: filterCount(overview.teams, filter),
                })}
              </Chip.Radio>
            ))}
          </div>
        </Fieldset>
        <Field data-size="sm" className={classes.search}>
          <Label htmlFor="team-search">{t('hrOverview.teams.search')}</Label>
          <Search>
            <Search.Input
              id="team-search"
              value={query.search}
              onChange={(event) =>
                setQuery({ ...query, search: event.target.value })
              }
            />
            <Search.Clear onClick={() => setQuery({ ...query, search: '' })} />
          </Search>
        </Field>
      </div>
      {shown.length === 0 ? (
        <Paragraph data-size="sm">{t('hrOverview.teams.noMatch')}</Paragraph>
      ) : (
        <div className={classes.tableScroll}>
          <Table data-size="sm" className={classes.table}>
            <Table.Head>
              <Table.Row>
                {columnsFor(overview.setup.usesSafetyReps).map((column) => (
                  <Table.HeaderCell key={column}>
                    {t(`hrOverview.teams.columns.${column}`)}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {shown.map((team) => {
                const route = teamRoute(team);
                return (
                  <Table.Row key={team.id}>
                    <Table.HeaderCell scope="row">
                      {route ? (
                        <Link asChild>
                          <RouterLink to={route}>{team.name}</RouterLink>
                        </Link>
                      ) : (
                        team.name
                      )}
                    </Table.HeaderCell>
                    <Table.Cell>{leadersText(team, t)}</Table.Cell>
                    {overview.setup.usesSafetyReps && (
                      <Table.Cell>
                        {team.safetyReps.length > 0 ? (
                          team.safetyReps.join(', ')
                        ) : (
                          <span className={classes.missing}>
                            {t('hrOverview.teams.missing')}
                          </span>
                        )}
                      </Table.Cell>
                    )}
                    <Table.Cell>
                      <Tag
                        data-size="sm"
                        data-color={COVERAGE_COLORS[team.coverage]}
                      >
                        {t(`hrOverview.coverage.statuses.${team.coverage}`)}
                      </Tag>
                    </Table.Cell>
                    <Table.Cell>{stepText(team, t)}</Table.Cell>
                    <Table.Cell
                      className={nextIsLate(team) ? classes.late : undefined}
                    >
                      {nextText(team, t, i18n.language)}
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        </div>
      )}
    </section>
  );
};

export const HRTeams = (): ReactElement => (
  <OverviewSubpage page="teams">
    {(overview) => <Teams overview={overview} />}
  </OverviewSubpage>
);
